import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

const API = "/ca/api/v1/auth";

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pw1 !== pw2) return setError(t("auth.resetMismatch"));
    if (pw1.length < 10) return setError(t("auth.resetTooShort"));
    setBusy(true);
    try {
      const res = await fetch(`${API}/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: pw1 }),
      });
      if (!res.ok) {
        let detail = t("auth.resetFailed");
        try {
          const d = await res.json();
          if (typeof d?.detail === "string") detail = d.detail;
        } catch { /* Antwort ohne JSON */ }
        setError(detail);
        return;
      }
      // Alte Sitzungen sind serverseitig beendet, lokale Reste mit wegräumen.
      localStorage.removeItem("ca_access_token");
      localStorage.removeItem("ca_refresh_token");
      setOk(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch {
      setError(t("auth.resetFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4">
      <h1 className="text-5xl font-serif font-semibold text-ca-brand tracking-tight mb-2">
        Nudging Bloom
      </h1>
      <p className="text-ca-ink-muted uppercase tracking-widest font-bold text-sm mb-12">
        {t("auth.tagline")}
      </p>

      <form
        onSubmit={submit}
        className="bg-ca-card rounded-3xl p-10 w-full max-w-md border border-ca-surface-border space-y-6"
      >
        <h2 className="text-2xl font-black text-ca-surface-ink uppercase tracking-tight">
          {t("auth.resetTitle")}
        </h2>

        {!token ? (
          <p className="text-verdict-red text-sm">{t("auth.resetNoToken")}</p>
        ) : ok ? (
          <p className="text-ca-surface-ink text-sm">{t("auth.resetDone")}</p>
        ) : (
          <>
            <div>
              <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
                {t("auth.resetNew")}
              </label>
              <input
                type="password"
                id="new-password"
                autoComplete="new-password"
                minLength={10}
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                className="w-full bg-white border border-ca-surface-border rounded-xl px-5 py-3 text-ca-surface-ink outline-none focus:border-ca-accent"
                required
              />
            </div>
            <div>
              <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
                {t("auth.resetRepeat")}
              </label>
              <input
                type="password"
                id="new-password-repeat"
                autoComplete="new-password"
                minLength={10}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="w-full bg-white border border-ca-surface-border rounded-xl px-5 py-3 text-ca-surface-ink outline-none focus:border-ca-accent"
                required
              />
            </div>
            {error && <p className="text-verdict-red text-sm">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full py-4 bg-ca-accent text-ca-brand font-black uppercase tracking-widest rounded-xl shadow-xl hover:scale-[1.02] disabled:opacity-50 transition-all active:scale-95"
            >
              {busy ? "..." : t("auth.resetButton")}
            </button>
          </>
        )}

        <p className="text-center text-sm text-ca-surface-muted">
          <Link to="/login" className="text-ca-surface-accent hover:underline">
            {t("auth.backToLogin")}
          </Link>
        </p>
      </form>
    </div>
  );
}
